import {Component, Material, Mesh, Object3D, Texture} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {Align, FlexDirection, Justify, PositionType} from '@wonderlandengine/react-ui';
import {
    Column,
    Container,
    Image,
    Panel,
    Plane,
    Row,
    Text,
} from '@wonderlandengine/react-ui/components';
import {vec3} from 'gl-matrix';
import {use, useEffect, useRef, useState} from 'react';
import {Localization} from '../../utils/Localization.js';
import {colorSwatch} from '../utils/colorSwatch.js';
import {UiTexturesManager} from '../components/ui-textures-manager.js';
import {useAnimation} from '../hooks/useAnimation.js';
import {Easing} from '@wonderlandengine/tweenjs';
import {RewardAwarder} from '../../components/RewardAwarder.js';
import {MenuButton} from '../components/menu-button.js';
import {goToPreviousMenu} from '../utils/goToPreviousMenu.js';
import {MenuPanels} from '../../models/UIModel.js';
import {insertNewlines} from '../utils/utils.js';
import {ColoredImage} from '../components/colored-image.js';
import {closeRewards} from '../utils/closeRewards.js';

export class CurtainsComponent extends Component {
    static TypeName = 'curtains-component';

    private static _instance: CurtainsComponent;
    static get instance(): CurtainsComponent {
        return CurtainsComponent._instance;
    }

    init() {
        if (CurtainsComponent._instance) {
            console.error('There can only be one instance of Curtains Component');
        }
        CurtainsComponent._instance = this;
    }

    @property.mesh()
    background!: Mesh;

    @property.mesh()
    borderLeftMesh!: Mesh;

    @property.mesh()
    borderRightMesh!: Mesh;

    @property.mesh()
    topMesh?: Mesh;

    @property.material()
    material?: Material;
}

function useRewardAwarder(): [
    string,
    boolean,
    [string, string, string],
    boolean,
    [Texture, Texture, Texture],
    number,
] {
    const rewardAwarder = RewardAwarder.instance;

    const [rewardDescription, setRewardDescription] = useState(
        rewardAwarder.rewardDescription || ''
    );
    const [showButton, setShowButton] = useState(false);
    const [rewardLabels, setRewardLabels] = useState<[string, string, string]>([
        '',
        '',
        '',
    ]);
    const [rewardTextures, setRewardTextures] = useState<[Texture, Texture, Texture]>([
        null,
        null,
        null,
    ]);

    const [rewardsGiven, setRewardsGiven] = useState(false);
    const [selectedReward, setSelectedReward] = useState(-1);

    useEffect(() => {
        const propChanged = (prop: string) => {
            switch (prop) {
                case 'rewardDescription':
                    setRewardDescription(rewardAwarder.rewardDescription);
                    break;
                case 'showCloseButton':
                    setShowButton(rewardAwarder.showCloseButton);
                    break;
                case 'rewardLabels':
                    setRewardLabels(rewardAwarder.rewardLabels);
                    break;
                case 'rewardsGiven':
                    setRewardsGiven(rewardAwarder.rewardsGiven);
                    setRewardTextures(rewardAwarder.rewardTextures);
                    setSelectedReward(rewardAwarder.selectedReward);
                    break;
            }
        };
        RewardAwarder.instance.propertyChanged.add(propChanged);
        return () => {
            RewardAwarder.instance.propertyChanged.remove(propChanged);
        };
    }, []);

    return [
        rewardDescription,
        showButton,
        rewardLabels,
        rewardsGiven,
        rewardTextures,
        selectedReward,
    ];
}

export const Rewards = () => {
    const [
        rewardDescription,
        showButton,
        rewardLabels,
        rewardsGiven,
        rewardTextures,
        selectedReward,
    ] = useRewardAwarder();

    const [leftCurtainRef, setLeftCurtainRef] = useAnimation({
        position: {from: [-3, 0], to: [3, 0]},
        duration: 1500,
        repeat: true,
        yoyo: true,
        easing: Easing.Sinusoidal.InOut,
    });
    const [rightCurtainRef, setRightCurtainRef] = useAnimation({
        position: {from: [-3, 0], to: [3, 0]},
        delay: 200,
        duration: 1368,
        repeat: true,
        yoyo: true,
        easing: Easing.Sinusoidal.InOut,
    });

    useEffect(() => {
        setRightCurtainRef(rightCurtainRef.current);
    }, [rightCurtainRef]);
    useEffect(() => {
        setLeftCurtainRef(leftCurtainRef.current);
    }, [leftCurtainRef]);

    return (
        <Container
            position={PositionType.Absolute}
            width="100%"
            height="100%"
            flexDirection={FlexDirection.Row}
            justifyContent={Justify.SpaceBetween}
        >
            <Container width={200}>
                <Plane
                    ref={leftCurtainRef}
                    mesh={CurtainsComponent.instance.borderLeftMesh}
                    material={CurtainsComponent.instance.material}
                    key="curtain-left"
                    height={200}
                    width={200}
                    top={-70}
                    left={-100}
                ></Plane>
            </Container>
            <Container
                marginTop={50}
                flexGrow={1}
                flexShrink={1}
                alignSelf={Align.Stretch}
                flexDirection={FlexDirection.Column}
                justifyContent={Justify.Center}
                alignItems={Align.Center}
            >
                <Panel
                    z={-0.03}
                    width={550}
                    height={60}
                    rounding={0}
                    backgroundColor="#3D2761"
                    justifyContent={Justify.Center}
                >
                    <Text fontSize={32} color="#7945DD" textAlign="center" z={-0.03}>
                        {Localization.instance.get('RewardScreenTitle')}
                    </Text>
                </Panel>
                <Container width={400} height={300} justifyContent={Justify.Center}>
                    {rewardsGiven && (
                        <Row
                            width="100%"
                            alignItems={Align.Center}
                            justifyContent={Justify.SpaceBetween}
                        >
                            <Column
                                width={150}
                                height={150}
                                justifyContent={Justify.Center}
                                gap={20}
                            >
                                <Image
                                    rounding={0}
                                    width={100}
                                    height={100}
                                    src={rewardTextures[0]}
                                ></Image>
                                {selectedReward !== 0 && (
                                    <ColoredImage
                                        position={PositionType.Absolute}
                                        color="#000000"
                                        rounding={0}
                                        width={125}
                                        height={125}
                                        src={RewardAwarder.instance.X}
                                    ></ColoredImage>
                                )}
                                <Text
                                    width={150}
                                    fontSize={20}
                                    textAlign="center"
                                    text={rewardLabels[0]}
                                ></Text>
                            </Column>
                            <Column
                                width={150}
                                height={150}
                                justifyContent={Justify.Center}
                                gap={20}
                            >
                                <Image
                                    rounding={0}
                                    width={100}
                                    height={100}
                                    src={rewardTextures[1]}
                                ></Image>
                                {selectedReward !== 1 && (
                                    <ColoredImage
                                        position={PositionType.Absolute}
                                        color="#000000"
                                        rounding={0}
                                        width={125}
                                        height={125}
                                        src={RewardAwarder.instance.X}
                                    ></ColoredImage>
                                )}
                                <Text
                                    width={150}
                                    fontSize={20}
                                    textAlign="center"
                                    text={rewardLabels[1]}
                                ></Text>
                            </Column>
                            <Column
                                width={150}
                                height={150}
                                justifyContent={Justify.Center}
                                gap={20}
                            >
                                <Image
                                    rounding={0}
                                    width={100}
                                    height={100}
                                    src={rewardTextures[2]}
                                ></Image>
                                {selectedReward !== 2 && (
                                    <ColoredImage
                                        position={PositionType.Absolute}
                                        color="#000000"
                                        rounding={0}
                                        width={125}
                                        height={125}
                                        src={RewardAwarder.instance.X}
                                    ></ColoredImage>
                                )}
                                <Text
                                    width={150}
                                    fontSize={20}
                                    textAlign="center"
                                    text={rewardLabels[2]}
                                ></Text>
                            </Column>
                        </Row>
                    )}
                </Container>
                <Panel
                    z={-0.03}
                    width={550}
                    height={100}
                    rounding={0}
                    backgroundColor="#3D2761"
                    justifyContent={Justify.FlexStart}
                >
                    <Text
                        marginTop={12}
                        z={-0.03}
                        fontSize={32}
                        color={colorSwatch.DescriptionText}
                        textAlign="center"
                        text={insertNewlines(rewardDescription, 18)}
                    ></Text>
                </Panel>
                <Row justifyContent={Justify.Center}>
                    {showButton ? (
                        <MenuButton
                            width={250}
                            height={54}
                            margin={6}
                            color={colorSwatch.AcceptButton}
                            fontSize={32}
                            onClick={() => closeRewards()}
                        >
                            {Localization.instance.get('PurchaseFailedDialogAccept')}
                        </MenuButton>
                    ) : (
                        <Container width={250} height={54} margin={6}></Container>
                    )}
                </Row>
            </Container>
            <Container width={200}>
                <Plane
                    ref={rightCurtainRef}
                    mesh={CurtainsComponent.instance.borderRightMesh}
                    material={CurtainsComponent.instance.material}
                    key="curtain-right"
                    height={200}
                    width={200}
                    top={-70}
                    left={100}
                ></Plane>
            </Container>
        </Container>
    );
};
